import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ButtonLoaderComponent } from '../../../../shared/components/button-loader/button-loader.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { UploadVideoComponent } from '../../../../shared/components/upload-video/upload-video.component';
import { MediaSignedUrlModel } from '../../../../shared/interface/interface';
import { VideoPayloadModel } from '../../../../shared/models/video.model';
import { VideoService } from '../../../../shared/services/video.service';
import { SharedModule } from '../../../../shared/shared.module';

@Component({
  selector: 'app-add-edit-video',
  standalone: true,
  imports: [
    SharedModule,
    ButtonComponent,
    ButtonLoaderComponent,
    UploadVideoComponent,
  ],
  templateUrl: './add-edit-video.component.html',
  styleUrl: './add-edit-video.component.scss',
})
export class AddEditVideoComponent implements OnInit, OnDestroy {
  videoForm: FormGroup = this.fb.group({
    title: ['', [Validators.required]],
    description: ['', [Validators.required]],
    videoUrl: ['', [Validators.required]],
  });
  isLoading = false;
  videoUploading = false;
  videoPreviewUrl = '';
  videoId = '';
  subscription = new Subscription();
  initialLoading = false;
  constructor(
    private fb: FormBuilder,
    private videoService: VideoService,
    private toastr: ToastrService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.subscribeToParamMap();
  }

  onSubmit() {
    if (this.videoForm.invalid) {
      this.videoForm.markAllAsTouched();
      return;
    }
    const { title, description, videoUrl } = this.videoForm.value;
    this.isLoading = true;
    if (this.videoId) {
      this.editVideo({ title, description, videoUrl });
    } else {
      this.addVideo({ title, description, videoUrl });
    }
  }

  cancel() {
    this.router.navigate(['/videos']);
  }

  onUploadEnd(data: MediaSignedUrlModel | null) {
    this.videoUploading = false;
    if (data) {
      this.videoForm.get('videoUrl')?.setValue(data.key);
      this.videoPreviewUrl = data.contentUrl;
    }
  }

  onUploadStart() {
    this.videoUploading = true;
    this.videoForm.get('videoUrl')?.markAsUntouched();
  }

  removeVideo() {
    this.videoPreviewUrl = '';
    this.videoForm.get('videoUrl')?.setValue('');
  }

  addVideo(payload: VideoPayloadModel) {
    this.videoService.addVideo(payload).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['/videos']);
        this.toastr.success('Video added successfully');
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
      },
    });
  }

  editVideo(payload: VideoPayloadModel) {
    this.videoService.updateVideo(this.videoId, payload).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['/videos']);
        this.toastr.success('Video updated successfully');
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
      },
    });
  }

  subscribeToParamMap() {
    const sub = this.activatedRoute.paramMap.subscribe((data: any) => {
      if (data.get('id')) {
        this.videoId = data.get('id');
        this.setInitialFormValues();
      }
    });
    this.subscription.add(sub);
  }

  setInitialFormValues() {
    this.initialLoading = true;
    this.videoService.fetchVideoById(this.videoId).subscribe({
      next: (data) => {
        this.initialLoading = false;
        this.videoForm.setValue({
          title: data.result.title,
          description: data.result.description,
          videoUrl: data.result.videoKey,
        });
        this.videoPreviewUrl = data.result.videoUrl;
      },
      error: (err) => {
        this.initialLoading = false;
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
      },
    });
  }
  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
